import { useCallback, useEffect, useState } from 'react';
import { List, Title, useListContext, useNotify, useRedirect } from 'react-admin';
import { useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Alert, CircularProgress,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  card, btn, tbl, typo, inputSx, badgeStyle, clickableA11y, NAVY, WEBSITE_PUBLICATION_STATE,
} from '../styles/ds';
import { websiteRequest } from './websiteApi';
import { PageTextEditor, PageBlocks, usePagePreview } from './PageTextEditor';

/*
 * Legal pages (WD-017): Shipping Policy, Returns & Refunds, Payment
 * Information, Privacy Policy, Terms & Conditions. Each has a draft and a
 * separately published copy; only approved text should be published.
 * The text format, toolbar and preview are in PageTextEditor.js. No HTML.
 */

const fmtDate = d => (d ? new Date(d).toLocaleDateString('he-IL') : '—');

const PagesTable = () => {
  const { data = [], isPending } = useListContext();
  const redirect = useRedirect();
  if (isPending) return <Box p={4} textAlign="center"><CircularProgress /></Box>;
  return (
    <TableContainer component={Box} sx={tbl.container}>
      <Box className="crm-table-scroll" data-mobile-scroll tabIndex={0} role="region" aria-label="דפי מדיניות"><Table>
        <TableHead><TableRow>
          <TableCell>דף</TableCell>
          <TableCell align="center">מצב</TableCell>
          <TableCell align="center">פורסם</TableCell>
          <TableCell>הערות</TableCell>
        </TableRow></TableHead>
        <TableBody>
          {data.map((p, i) => (
            <TableRow key={p.id} {...clickableA11y(() => redirect('edit', 'website_pages', p.id), { ariaLabel: `עריכת ${p.title_en}` })}
              onClick={() => redirect('edit', 'website_pages', p.id)} sx={tbl.bodyRow(i)}>
              <TableCell>
                <Typography fontWeight={600} fontSize={14} dir="ltr" textAlign="right">{p.title_en}</Typography>
                <Typography fontSize={11} color="text.secondary" dir="ltr" textAlign="right">/pages/{p.slug}</Typography>
              </TableCell>
              <TableCell align="center">
                <span style={badgeStyle(WEBSITE_PUBLICATION_STATE, p.publication_state)}>{WEBSITE_PUBLICATION_STATE[p.publication_state]?.label}</span>
              </TableCell>
              <TableCell align="center">{fmtDate(p.published_at)}</TableCell>
              <TableCell>
                <Typography fontSize={12} color={p.version === 0 ? 'warning.dark' : 'text.secondary'}>
                  {p.version === 0 ? 'טרם נכתב' : p.has_unpublished_changes ? 'יש שינויים בטיוטה שטרם פורסמו' : ''}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table></Box>
    </TableContainer>
  );
};

export const WebsitePageList = () => (
  <List title="דפי מדיניות באתר" pagination={false} actions={false} storeKey={false} sx={tbl.listSx}
    className="crm-mobile-list crm-inline-list">
    <Typography variant="body2" color="text.secondary" mb={2}>
      חמשת דפי המדיניות של האתר. יש לפרסם רק טקסט שאושר (משפטית/עסקית) — הטקסט לא נכתב אוטומטית.
    </Typography>
    <PagesTable />
  </List>
);

const ACTIONS = {
  publish: { title: 'פרסום הדף', text: 'הטיוטה הנוכחית תוצג באתר לכל המבקרים. יש לוודא שהטקסט אושר. להמשיך?', label: 'פרסום', done: 'הדף פורסם' },
  unpublish: { title: 'הסרה מפרסום', text: 'הדף יוסר מהאתר מיד (הטקסט נשמר). להמשיך?', label: 'הסרה מפרסום', done: 'הדף הוסר מפרסום' },
};

export const WebsitePageEdit = () => {
  const { id } = useParams();
  const notify = useNotify();
  const redirect = useRedirect();
  const [page, setPage] = useState(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState(null);
  const preview = usePagePreview(id, title, body);

  const apply = p => { setPage(p); setTitle(p.title_en); setBody(p.draft_body); };
  const load = useCallback(() => {
    websiteRequest(`/website_pages/${id}`).then(apply).catch(err => notify(err.message, { type: 'error' }));
  }, [id, notify]);
  useEffect(() => { load(); }, [load]);

  if (!page) return <Box p={6} textAlign="center"><CircularProgress /></Box>;
  const dirty = title !== page.title_en || body !== page.draft_body;
  const problems = preview.problems ?? [];

  const saveDraft = async () => {
    setBusy(true);
    try {
      apply(await websiteRequest(`/website_pages/${page.slug}`, 'PUT', { title_en: title, draft_body: body, version: page.version }));
      notify('הטיוטה נשמרה', { type: 'success' });
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setBusy(false); }
  };
  const runAction = async () => {
    setBusy(true);
    try {
      apply(await websiteRequest(`/website_pages/${page.slug}/publication`, 'POST', { action: pending, version: page.version }));
      notify(ACTIONS[pending].done, { type: 'success' });
      setPending(null);
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setBusy(false); }
  };
  const published = page.publication_state === 'published';

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Title title={`דף מדיניות: ${page.title_en}`} />
      <Button startIcon={<ArrowForwardIcon />} onClick={() => redirect('list', 'website_pages')} sx={{ mb: 1 }}>חזרה לדפי המדיניות</Button>
      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap mb={2}>
            <Typography sx={typo.sectionTitle} dir="ltr">{page.title_en}</Typography>
            <span style={badgeStyle(WEBSITE_PUBLICATION_STATE, page.publication_state)}>{WEBSITE_PUBLICATION_STATE[page.publication_state]?.label}</span>
            {published && <Typography fontSize={12} color="text.secondary">פורסם ב-{fmtDate(page.published_at)}</Typography>}
          </Stack>
          {page.has_unpublished_changes && (
            <Alert severity="warning" sx={{ mb: 2 }}>בטיוטה יש שינויים שעדיין לא פורסמו — באתר מוצג הנוסח שפורסם קודם.</Alert>
          )}
          <Alert severity="info" sx={{ mb: 2 }}>
            יש לפרסם רק טקסט מאושר. לעיצוב משתמשים בסרגל הכלים (מודגש, קישור, כותרות, רשימות); שורה ריקה מתחילה פסקה חדשה. התצוגה המקדימה מראה בדיוק את מה שיופיע באתר.
          </Alert>
          <Stack spacing={2}>
            <TextField label="כותרת (אנגלית)" value={title} onChange={e => setTitle(e.target.value)} disabled={busy}
              inputProps={{ dir: 'ltr' }} sx={inputSx} fullWidth />
            <PageTextEditor value={body} onChange={setBody} disabled={busy} />
            {problems.length > 0 && (
              <Alert severity="error">
                קישורים לא מותרים — יש לתקן לפני שמירה. מותר רק קישור מאובטח (https), מייל, טלפון או דף באתר.
                {problems.map((p, i) => <Box key={i}>שורה {p.line}: <bdi dir="ltr">{p.href || '(ריק)'}</bdi></Box>)}
              </Alert>
            )}
            <Typography sx={typo.sectionTitle}>תצוגה מקדימה של הטיוטה</Typography>
            {preview.error ? <Alert severity="warning">לא ניתן להציג תצוגה מקדימה: {preview.error}</Alert>
              : <PageBlocks title={preview.title ?? title} blocks={preview.blocks} />}
          </Stack>
          <Stack direction="row" spacing={1.5} mt={2.5} flexWrap="wrap" useFlexGap>
            <Button variant="contained" disabled={busy || !dirty || !title.trim() || problems.length > 0} onClick={saveDraft} sx={btn.primary}>שמירת טיוטה</Button>
            <Button variant="outlined" disabled={busy || dirty || page.version === 0 || !page.draft_body.trim() || (published && !page.has_unpublished_changes)}
              onClick={() => setPending('publish')} sx={btn.gold}>
              {published ? 'פרסום השינויים' : 'פרסום באתר'}
            </Button>
            {published && (
              <Button variant="outlined" disabled={busy} onClick={() => setPending('unpublish')} sx={btn.cancel}>הסרה מפרסום</Button>
            )}
            {dirty && <Typography sx={typo.meta}>יש לשמור את הטיוטה לפני פרסום.</Typography>}
          </Stack>
        </CardContent>
      </Card>

      {pending && (
        <Dialog open onClose={() => setPending(null)} dir="rtl" maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>{ACTIONS[pending].title}</DialogTitle>
          <DialogContent dividers><Typography fontSize={14}>{ACTIONS[pending].text}</Typography></DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button variant="outlined" onClick={() => setPending(null)} sx={btn.cancel}>ביטול</Button>
            <Button variant="contained" onClick={runAction} disabled={busy} sx={btn.primary}>{ACTIONS[pending].label}</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
